import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { Badge, Avatar, Typography, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
// Ícones no estilo "Rounded" (mais modernos). Os nomes locais continuam os
// mesmos para não mexer no resto do arquivo.
import DashboardOutlinedIcon from "@material-ui/icons/DashboardRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAltRounded";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsRounded";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneRounded";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/FlashOnRounded";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryRounded";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferRounded";
import DeviceHubOutlinedIcon from "@material-ui/icons/AccountTreeRounded";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import EventOutlinedIcon from "@material-ui/icons/EventRounded";
import AllInclusiveRoundedIcon from "@material-ui/icons/AllInclusiveRounded";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import api from "../services/api";
import openSocket from "../services/socket-io";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

const useStyles = makeStyles((theme) => ({
  // Barra lateral clara (corporativa): item ativo com fundo verde-agua
  // claro e uma barrinha na esquerda. Sem brilhos nem orbitas.
  listItem: {
    borderRadius: 10,
    marginBottom: 2,
    padding: "6px 10px",
    color: theme.palette.text.secondary,
    transition: "background-color .15s ease, color .15s ease",
    "& .MuiListItemIcon-root": { minWidth: 42 },
    "& .MuiListItemText-primary": {
      fontSize: "0.86rem",
      fontWeight: 600,
    },
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f2f7f7",
      "& .MuiListItemIcon-root, & svg": { color: "#0d9488 !important" },
      "& .MuiListItemText-primary": { color: theme.palette.text.primary },
    },
  },

  // Entrada em cascata dos itens (uma vez so, quando a barra aparece).
  menuAnimado: {
    "& li": { animation: "sbEntrar .35s cubic-bezier(.22,1,.36,1) both" },
    ...Object.fromEntries(
      Array.from({ length: 16 }, (_, i) => [
        `& li:nth-of-type(${i + 1})`,
        { animationDelay: `${0.03 + i * 0.03}s` },
      ])
    ),
  },
  listItemActive: {
    position: "relative",
    borderRadius: 10,
    marginBottom: 2,
    padding: "6px 10px",
    paddingRight: 12,
    background: (theme.palette.type === "dark" ? "rgba(45,212,191,0.12)" : "#e6f6f4") + " !important",
    color: (theme.palette.type === "dark" ? "#5eead4" : "#0f766e") + " !important",
    "& .MuiListItemIcon-root": { minWidth: 42, position: "relative" },
    // barrinha verde-agua na esquerda
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: 3,
      background: "#0d9488",
    },
    "&:hover": {
      background: (theme.palette.type === "dark" ? "rgba(45,212,191,0.16)" : "#d9f2ef") + " !important",
    },
    "& .MuiListItemIcon-root, & svg": {
      color: (theme.palette.type === "dark" ? "#2dd4bf" : "#0d9488") + " !important",
      fill: "currentColor",
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.86rem",
      fontWeight: 700,
      color: theme.palette.type === "dark" ? "#e6fffb" : "#0f2a28",
    },
  },
  // Barra recolhida: o texto sai e o icone passa a ser centralizado dentro do
  // retangulo da selecao. Sem isso o ListItemText continua ocupando espaco
  // (so cortado pelo overflow) e empurra o icone para a esquerda do realce.
  // O texto e as bolinhas dividiam o mesmo espaco sem ninguem ceder, e as
  // bolinhas acabavam por cima da ultima letra. Agora o texto encolhe (com
  // reticencias, se precisar) e a area das bolinhas nunca e comprimida.
  itemTexto: {
    minWidth: 0,
    marginRight: 8,
    "& .MuiListItemText-primary": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  areaBadges: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 6,
    marginLeft: "auto",
  },
  railColapsada: {
    "& .MuiListItem-root": {
      // padding zerado nos DOIS lados: qualquer sobra de recuo herdada do MUI
      // (gutters) ou do padding curto de listItem/listItemActive deixaria mais
      // verde de um lado do icone do que do outro.
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0,
    },
    "& .MuiListItemIcon-root": {
      // ocupa a largura toda do item e centraliza o svg dentro dela, em vez de
      // depender da largura natural do icone.
      minWidth: 0,
      width: "100%",
      margin: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    "& .MuiListItemText-root": {
      display: "none",
    },
    // recolhida, o icone fica centralizado: a orbita sairia do lugar
    "& .MuiListItemIcon-root::after": { display: "none" },
    "& .MuiListItem-root::before": { display: "none" },
    // as bolinhas de contagem tambem saem: com o texto escondido elas
    // desalinhariam o icone. O ripple do MUI fica de fora da regra.
    "& .MuiListItem-root > span:not(.MuiTouchRipple-root)": {
      display: "none",
    },
  },
  // ícone dentro de uma caixinha arredondada
  listItemIcon: {
    width: 34,
    height: 34,
    padding: 8,
    borderRadius: 10,
    color: theme.palette.type === "dark" ? "#94a3b8" : "#6b7f7c",
    backgroundColor: "transparent",
    transition: "color .15s ease",
  },
  listItemText: {
    "& .MuiListItemText-primary": {
      fontSize: "0.85rem",

      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },
  sectionTitle: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "rgba(226,232,240,0.42)" : "#8a9a98",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "20px 16px 8px 16px",
  },
  divider: {
    margin: "8px 16px",
    backgroundColor: theme.palette.divider,
  },
  bottomSection: {
    borderTop: "1px solid rgba(148,163,184,0.12)",
    paddingTop: 8,
    marginTop: "auto",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderTop: "1px solid rgba(148,163,184,0.12)",
    marginTop: 8,
  },
  userAvatar: {
    width: 36,
    height: 36,
    backgroundColor: "#0D9488",
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  userInfo: {
    marginLeft: 12,
    flex: 1,
    overflow: "hidden",
  },
  userName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: "0.75rem",
    color: "#94a3b8",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const CORES_BOLINHA = {
  ia: "#f59e0b",
  humano: "#2563eb",
};

const Bolinha = ({ valor, tipo, titulo }) => {
  if (!valor) return null;

  return (
    <span
      title={titulo}
      style={{
        backgroundColor: CORES_BOLINHA[tipo],
        color: "#fff",
        borderRadius: 10,
        fontSize: "0.68rem",
        fontWeight: 700,
        lineHeight: "18px",
        minWidth: 20,
        height: 18,
        padding: "0 6px",
        textAlign: "center",
        display: "inline-block",
        flexShrink: 0,
      }}
    >
      {valor > 99 ? "99+" : valor}
    </span>
  );
};

function ListItemLink(props) {
  const { icon, primary, to, className, badges } = props;
  const classes = useStyles();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li style={{ listStyle: "none" }}>
      <ListItem button component={renderLink} className={className}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} className={classes.itemTexto} />
        {badges ? <span className={classes.areaBadges}>{badges}</span> : null}
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose, drawerOpen } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, handleLogout } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [contadores, setContadores] = useState({ espera: 0, humano: 0 });
  const location = useLocation();

  
  useEffect(() => {
    let vivo = true;

    const buscar = async () => {
      try {
        const { data } = await api.get("/tickets/contadores");
        if (vivo) setContadores(data);
      } catch {
        
      }
    };

    buscar();

    const socket = openSocket();

    

    
    const aplicar = (dados) => {
      if (!vivo || !dados) return;
      setContadores({
        espera: Number(dados.espera) || 0,
        humano: Number(dados.humano) || 0,
      });
    };

    socket.on("ticketCounters", aplicar);
    socket.on("ticket", buscar);
    socket.on("aiNotification", buscar);

    return () => {
      vivo = false;
      socket.off("ticketCounters", aplicar);
      socket.off("ticket", buscar);
      socket.off("aiNotification", buscar);

      

    };
  }, []);

  const classes = useStyles();

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {

        

        

        

        

        // Número inativo não é problema: foi o admin que desligou, de propósito.
        // Contá-lo aqui deixava o "!" aceso para sempre depois de aposentar um
        // chip banido, e o alerta perdia a graça — quem vê sempre, não vê.
        const offlineWhats = whatsApps.filter((whats) => {
          if (whats.ativa === false) return false;
          return (
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      } else {
        // Sem nenhuma conexão cadastrada não há o que alertar. Antes o estado
        // antigo ficava preso: excluir a última conexão não apagava o "!".
        setConnectionWarning(false);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const getItemClass = (path) =>
    isActive(path) ? classes.listItemActive : classes.listItem;

  const getIconClass = () => classes.listItemIcon;

  return (
    <div
      onClick={drawerClose}
      className={`sb-anima ${classes.menuAnimado} ${drawerOpen ? "" : classes.railColapsada}`}
      style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0 8px" }}
    >
      <div style={{ flex: 1 }}>
        {drawerOpen && (
          <Typography className={classes.sectionTitle}>
            Menu
          </Typography>
        )}

        <ListItemLink
          to="/"
          primary="Dashboard"
          icon={<DashboardOutlinedIcon className={getIconClass()} />}
          className={getItemClass("/")}
        />
        <ListItemLink
          to="/atendimentos"
          primary={i18n.t("mainDrawer.listItems.tickets")}
          icon={<WhatsAppIcon className={getIconClass()} />}
          className={getItemClass("/atendimentos")}
          badges={
            <>
              <Bolinha
                valor={contadores.espera}
                tipo="ia"
                titulo={`${contadores.espera} candidato(s) na espera — a assistente está atendendo`}
              />
              <Bolinha
                valor={contadores.humano}
                tipo="humano"
                titulo={`${contadores.humano} candidato(s) precisando de atendimento humano`}
              />
            </>
          }
        />
        <ListItemLink
          to="/agenda"
          primary="Agenda"
          icon={<EventOutlinedIcon className={getIconClass()} />}
          className={getItemClass("/agenda")}
        />
        <ListItemLink
          to="/contatos"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<ContactPhoneOutlinedIcon className={getIconClass()} />}
          className={getItemClass("/contatos")}
        />
        <ListItemLink
          to="/respostas-rapidas"
          primary={i18n.t("mainDrawer.listItems.quickAnswers")}
          icon={<QuestionAnswerOutlinedIcon className={getIconClass()} />}
          className={getItemClass("/respostas-rapidas")}
        />
        <ListItemLink
          to="/etiquetas"
          primary="Etiquetas"
          icon={<LocalOfferOutlinedIcon className={getIconClass()} />}
          className={getItemClass("/etiquetas")}
        />


        <Can
          role={user.profile}
          perform="drawer-admin-items:view"
          yes={() => (
            <>
              {drawerOpen && (
                <Typography className={classes.sectionTitle}>
                  {i18n.t("mainDrawer.listItems.administration")}
                </Typography>
              )}

              {(user.profile === "admin" || user.profile === "operador") && (
              <ListItemLink
                to="/conexoes"
                primary={i18n.t("mainDrawer.listItems.connections")}
                icon={
                  <Badge badgeContent={connectionWarning ? "!" : 0} color="error" overlap="rectangular">
                    <SyncAltIcon className={getIconClass()} />
                  </Badge>
                }
                className={getItemClass("/conexoes")}
              />
              )}
              {}
              {user.profile === "admin" && (
              <ListItemLink
                to="/fluxos-visuais"
                primary="Fluxos Visuais"
                icon={<DeviceHubOutlinedIcon className={getIconClass()} />}
                className={getItemClass("/fluxos-visuais")}
              />
              )}
              {user.profile === "admin" && (
              <ListItemLink
                to="/chatbot-ia"
                primary="Assistente IA"
                icon={<MemoryOutlinedIcon className={getIconClass()} />}
                className={getItemClass("/chatbot-ia")}
              />
              )}
              {user.profile === "admin" && (
              <ListItemLink
                to="/meta"
                primary="Meta"
                icon={<AllInclusiveRoundedIcon className={getIconClass()} />}
                className={getItemClass("/meta")}
              />
              )}
              <ListItemLink
                to="/configuracoes"
                primary={i18n.t("mainDrawer.listItems.settings")}
                icon={<SettingsOutlinedIcon className={getIconClass()} />}
                className={getItemClass("/configuracoes")}
              />
            </>
          )}
        />
      </div>

    </div>
  );
};

export default MainListItems;
